/**
 * Dashboard Data Context - Frontend
 * @description Context for caching dashboard data between page navigations
 * Optimizes data fetching by storing devices, operations, and lectures in memory
 */

import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { DashboardDevice, DashboardOperation, DashboardLecture } from '@/types/dashboard.types';
// Context state interface
interface DashboardDataState {
  // Devices data
  devices: DashboardDevice[];
  devicesLoading: boolean;
  devicesLastFetch?: Date;

  // Operations data (keyed by deviceId)
  operationsByDevice: Record<string, DashboardOperation[]>;
  operationsLoading: Record<string, boolean>;
  operationsLastFetch: Record<string, Date>;

  // Lectures data (keyed by operationId)
  lecturesByOperation: Record<string, DashboardLecture[]>;
  lecturesLoading: Record<string, boolean>;
  lecturesLastFetch: Record<string, Date>;

  // Cache metadata
  cacheExpiry: number; // in milliseconds
}

// Context actions interface
interface DashboardDataActions {
  // Devices actions
  setDevices: (devices: DashboardDevice[]) => void;
  setDevicesLoading: (loading: boolean) => void;
  clearDevices: () => void;

  // Operations actions
  setOperationsForDevice: (deviceId: string, operations: DashboardOperation[]) => void;
  setOperationsLoading: (deviceId: string, loading: boolean) => void;
  getOperationsForDevice: (deviceId: string) => DashboardOperation[] | undefined;
  clearOperationsForDevice: (deviceId: string) => void;
  clearAllOperations: () => void;

  // Lectures actions
  setLecturesForOperation: (operationId: string, lectures: DashboardLecture[]) => void;
  setLecturesLoading: (operationId: string, loading: boolean) => void;
  getLecturesForOperation: (operationId: string) => DashboardLecture[] | undefined;
  clearLecturesForOperation: (operationId: string) => void;
  clearAllLectures: () => void;

  // Cache management
  isDataStale: (key: string, type: 'devices' | 'operations' | 'lectures') => boolean;
  clearAllCache: () => void;
  setCacheExpiry: (expiry: number) => void;
}

// Combined context type
type DashboardDataContextType = DashboardDataState & DashboardDataActions;

// Create context
const DashboardDataContext = createContext<DashboardDataContextType | undefined>(undefined);

// Provider component
interface DashboardDataProviderProps {
  children: ReactNode;
  initialCacheExpiry?: number; // Default: 5 minutes
}

export const DashboardDataProvider: React.FC<DashboardDataProviderProps> = ({
  children,
  initialCacheExpiry = 5 * 60 * 1000, // 5 minutes
}) => {
  const [state, setState] = useState<DashboardDataState>({
    devices: [],
    devicesLoading: false,
    devicesLastFetch: undefined,

    operationsByDevice: {},
    operationsLoading: {},
    operationsLastFetch: {},

    lecturesByOperation: {},
    lecturesLoading: {},
    lecturesLastFetch: {},

    cacheExpiry: initialCacheExpiry,
  });

  // Devices actions
  const setDevices = useCallback((devices: DashboardDevice[]) => {
    setState((prev) => ({
      ...prev,
      devices,
      devicesLastFetch: new Date(),
    }));
  }, []);

  const setDevicesLoading = useCallback((loading: boolean) => {
    setState((prev) => ({
      ...prev,
      devicesLoading: loading,
    }));
  }, []);

  const clearDevices = useCallback(() => {
    setState((prev) => ({
      ...prev,
      devices: [],
      devicesLastFetch: undefined,
    }));
  }, []);

  // Operations actions
  const setOperationsForDevice = useCallback(
    (deviceId: string, operations: DashboardOperation[]) => {
      setState((prev) => ({
        ...prev,
        operationsByDevice: {
          ...prev.operationsByDevice,
          [deviceId]: operations,
        },
        operationsLastFetch: {
          ...prev.operationsLastFetch,
          [deviceId]: new Date(),
        },
      }));
    },
    []
  );

  const setOperationsLoading = useCallback((deviceId: string, loading: boolean) => {
    setState((prev) => ({
      ...prev,
      operationsLoading: {
        ...prev.operationsLoading,
        [deviceId]: loading,
      },
    }));
  }, []);

  const getOperationsForDevice = useCallback(
    (deviceId: string) => {
      return state.operationsByDevice[deviceId];
    },
    [state.operationsByDevice]
  );

  const clearOperationsForDevice = useCallback((deviceId: string) => {
    setState((prev) => {
      const newOperationsByDevice = { ...prev.operationsByDevice };
      const newOperationsLoading = { ...prev.operationsLoading };
      const newOperationsLastFetch = { ...prev.operationsLastFetch };

      delete newOperationsByDevice[deviceId];
      delete newOperationsLoading[deviceId];
      delete newOperationsLastFetch[deviceId];

      return {
        ...prev,
        operationsByDevice: newOperationsByDevice,
        operationsLoading: newOperationsLoading,
        operationsLastFetch: newOperationsLastFetch,
      };
    });
  }, []);

  const clearAllOperations = useCallback(() => {
    setState((prev) => ({
      ...prev,
      operationsByDevice: {},
      operationsLoading: {},
      operationsLastFetch: {},
    }));
  }, []);

  // Lectures actions
  const setLecturesForOperation = useCallback(
    (operationId: string, lectures: DashboardLecture[]) => {
      setState((prev) => ({
        ...prev,
        lecturesByOperation: {
          ...prev.lecturesByOperation,
          [operationId]: lectures,
        },
        lecturesLastFetch: {
          ...prev.lecturesLastFetch,
          [operationId]: new Date(),
        },
      }));
    },
    []
  );

  const setLecturesLoading = useCallback((operationId: string, loading: boolean) => {
    setState((prev) => ({
      ...prev,
      lecturesLoading: {
        ...prev.lecturesLoading,
        [operationId]: loading,
      },
    }));
  }, []);

  const getLecturesForOperation = useCallback(
    (operationId: string) => {
      return state.lecturesByOperation[operationId];
    },
    [state.lecturesByOperation]
  );

  const clearLecturesForOperation = useCallback((operationId: string) => {
    setState((prev) => {
      const newLecturesByOperation = { ...prev.lecturesByOperation };
      const newLecturesLoading = { ...prev.lecturesLoading };
      const newLecturesLastFetch = { ...prev.lecturesLastFetch };

      delete newLecturesByOperation[operationId];
      delete newLecturesLoading[operationId];
      delete newLecturesLastFetch[operationId];

      return {
        ...prev,
        lecturesByOperation: newLecturesByOperation,
        lecturesLoading: newLecturesLoading,
        lecturesLastLastFetch: newLecturesLastFetch,
      };
    });
  }, []);

  const clearAllLectures = useCallback(() => {
    setState((prev) => ({
      ...prev,
      lecturesByOperation: {},
      lecturesLoading: {},
      lecturesLastFetch: {},
    }));
  }, []);

  // Cache management
  const isDataStale = useCallback(
    (key: string, type: 'devices' | 'operations' | 'lectures') => {
      const now = new Date();
      let lastFetch: Date | undefined;

      switch (type) {
        case 'devices':
          lastFetch = state.devicesLastFetch;
          break;
        case 'operations':
          lastFetch = state.operationsLastFetch[key];
          break;
        case 'lectures':
          lastFetch = state.lecturesLastFetch[key];
          break;
      }

      if (!lastFetch) return true;

      const timeDiff = now.getTime() - lastFetch.getTime();
      return timeDiff > state.cacheExpiry;
    },
    [state.devicesLastFetch, state.operationsLastFetch, state.lecturesLastFetch, state.cacheExpiry]
  );

  const clearAllCache = useCallback(() => {
    setState((prev) => ({
      ...prev,
      devices: [],
      devicesLastFetch: undefined,
      operationsByDevice: {},
      operationsLoading: {},
      operationsLastFetch: {},
      lecturesByOperation: {},
      lecturesLoading: {},
      lecturesLastFetch: {},
    }));
  }, []);

  const setCacheExpiry = useCallback((expiry: number) => {
    setState((prev) => ({
      ...prev,
      cacheExpiry: expiry,
    }));
  }, []);

  const contextValue: DashboardDataContextType = {
    // State
    ...state,

    // Actions
    setDevices,
    setDevicesLoading,
    clearDevices,
    setOperationsForDevice,
    setOperationsLoading,
    getOperationsForDevice,
    clearOperationsForDevice,
    clearAllOperations,
    setLecturesForOperation,
    setLecturesLoading,
    getLecturesForOperation,
    clearLecturesForOperation,
    clearAllLectures,
    isDataStale,
    clearAllCache,
    setCacheExpiry,
  };

  return (
    <DashboardDataContext.Provider value={contextValue}>{children}</DashboardDataContext.Provider>
  );
};

// Custom hook to use the context
export const useDashboardData = (): DashboardDataContextType => {
  const context = useContext(DashboardDataContext);
  if (context === undefined) {
    throw new Error('useDashboardData must be used within a DashboardDataProvider');
  }
  return context;
};

// Export types for use in other components
// export type { DashboardDevice, DashboardOperation, DashboardLecture };
